/**
 * Pilotes de saisie : comment une grandeur évolue sur une période.
 *
 * Un scénario prévisionnel se règle grandeur par grandeur — effectif adhérent,
 * produit moyen, chaque ressource conventionnelle. Chacune se saisit de l'une
 * de quatre façons, et c'est la seule chose qui les distingue. Le pilote est
 * donc la brique élémentaire du modèle de scénario.
 *
 * Le type est une **somme** et non un objet à champs optionnels : le mode et
 * ses paramètres sont indissociables. Sans cela on pourrait construire un
 * `{ mode: 'cible' }` sans cible, que le compilateur accepterait et que
 * l'interface afficherait à zéro.
 */
/** Modes de saisie reconnus. */
export const MODES_PILOTE = ['annee_par_annee', 'cible', 'constant', 'indexe'];
/**
 * Résout un pilote en une série alignée sur `exercices`.
 *
 * `depart` est la valeur constatée à l'exercice de départ : elle sert d'origine
 * à l'interpolation du mode `cible`, et n'est utilisée par aucun autre mode.
 *
 * Un pilote rend une **valeur d'inducteur**, pas un montant comptabilisé : un
 * produit moyen par adhérent est un prix unitaire. L'arrondi se fait donc à six
 * décimales, et non à la centime : arrondir 442,111730 € à 442,11 € avant de
 * multiplier par 208 adhérents perd 36 centimes sur le total des cotisations.
 * C'est `evaluerScenario` qui arrondit à la centime, là où naît le montant.
 * Six décimales suffisent en outre à absorber la dérive des flottants.
 *
 * En mode `cible`, l'interpolation court de `depart` — situé au premier
 * exercice de la série — jusqu'à la cible, atteinte à `exerciceCible`. Au-delà
 * de cet exercice la valeur se maintient : une cible n'est pas une pente qu'on
 * prolonge indéfiniment. Une cible antérieure ou égale au premier exercice
 * s'applique dès celui-ci.
 */
export function resoudrePilote(p, exercices, depart) {
    const arrondir = (v) => Math.round(v * 1e6) / 1e6;
    switch (p.mode) {
        case 'annee_par_annee':
            return exercices.map((_, i) => arrondir(p.valeurs[i] ?? p.valeurs[p.valeurs.length - 1] ?? 0));
        case 'constant':
            return exercices.map(() => arrondir(p.valeur));
        case 'indexe':
            return exercices.map((_, i) => arrondir(p.valeur * (1 + p.taux) ** i));
        case 'cible': {
            const premier = exercices[0];
            if (premier === undefined)
                return [];
            const pas = p.exerciceCible - premier;
            if (pas <= 0)
                return exercices.map(() => arrondir(p.cible));
            return exercices.map((annee) => {
                const rang = Math.min(annee - premier, pas);
                return arrondir(depart + ((p.cible - depart) * rang) / pas);
            });
        }
    }
}
/**
 * Valeur d'un pilote à un exercice donné. Raccourci de lecture ; passe par
 * `resoudrePilote` pour qu'il n'existe qu'une règle d'interpolation.
 */
export function valeurA(p, exercices, depart, exercice) {
    const i = exercices.indexOf(exercice);
    if (i < 0)
        return 0;
    return resoudrePilote(p, exercices, depart)[i] ?? 0;
}
/** Description en français d'un pilote, pour l'interface et les exports. */
export function decrirePilote(p, unite) {
    const nombre = (v) => {
        if (unite === 'taux')
            return `${(v * 100).toFixed(1).replace('.', ',')} %`;
        if (unite === 'effectif')
            return String(Math.round(v));
        return `${Math.round(v)} €`;
    };
    switch (p.mode) {
        case 'annee_par_annee':
            return 'arbitré exercice par exercice';
        case 'cible':
            return `cible de ${nombre(p.cible)} atteinte en ${p.exerciceCible}`;
        case 'constant':
            return `${nombre(p.valeur)} sur toute la période`;
        case 'indexe':
            return `${nombre(p.valeur)} au départ, ${(p.taux * 100).toFixed(1).replace('.', ',')} % par an`;
    }
}
/** Pilote constant, forme la plus simple — sert de valeur par défaut. */
export function piloteConstant(valeur) {
    return { mode: 'constant', valeur };
}
//# sourceMappingURL=pilote.js.map